import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';

import { MultimediaRoomService } from '../services/multimedia-room.service';
import { SceneService } from '../services/scene.service';
import { ZoomDirection } from '../social-pixi-canvas/scene-manager';
import { BrowserService } from '../services/browser.service';

@Component({
  selector: 'app-zoom-pixi-world-controls',
  templateUrl: './zoom-pixi-world-controls.component.html',
  styleUrls: ['./zoom-pixi-world-controls.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomPixiWorldControlsComponent implements OnDestroy {
  @Input() public showZoomControls = true;
  public showZoomFlag = true;
  public autoZoomFlag = false;
  public autoZoomEnabled = false;
  public inDisabled = false;
  public outDisabled = false;
  public zoomDirection: ZoomDirection = { value: 'none' };
  private zoomIntervalId!: NodeJS.Timeout | null;
  private isZooming = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected pinchZoomEnabled = false;
  private socialAudioRelative = false;

  public isMobile = this.browserService?.isMobileOrTablet ?? false;

  public constructor(
    private sceneService: SceneService,
    private browserService: BrowserService,
    private store: Store<AppState>,
    private multimediaRoomService: MultimediaRoomService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showZoomFlag) => {
        this.showZoomFlag = showZoomFlag;
        changeDetectorRef.markForCheck();
      });
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_auto_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((autoZoomFlag) => {
        this.autoZoomFlag = autoZoomFlag;
        this.autoZoomEnabled = autoZoomFlag;
        if (this.sceneService.sceneManager) {
          this.sceneService.sceneManager.autoZoomEnabled = autoZoomFlag;
        }
        changeDetectorRef.markForCheck();
      });
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pinch_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((pinchZoom) => {
        this.pinchZoomEnabled = pinchZoom;
        changeDetectorRef.markForCheck();
      });
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_relative)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialAudioRelative) => {
        this.socialAudioRelative = socialAudioRelative;
        changeDetectorRef.markForCheck();
      });

    sceneService?.sceneManager?.zoomDirection$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.zoomDirection = value;
      changeDetectorRef.markForCheck();
    });
  }

  public autoZoomTooltip(): string {
    return `Auto Zoom is ${this.autoZoomEnabled ? 'on' : 'off'}`;
  }

  public toggleAutoZoom(forceOff = false): void {
    const sm = this.sceneService.sceneManager;
    if (!sm) {
      return;
    }
    this.autoZoomEnabled = sm.toggleAutoZoom(forceOff);
  }

  public checkBounds(): void {
    const sm = this.sceneService.sceneManager;
    if (!sm) {
      return;
    }
    this.inDisabled = sm.currentZoom >= sm.maxZoom;
    this.outDisabled = sm.currentZoom <= sm.minZoom;
  }

  public startZoom(zoomIn: boolean): void {
    // Immediately trigger the zoom action
    void this.zoom(zoomIn);

    // Set up an interval for continuous zooming
    this.zoomIntervalId = setInterval(() => {
      void this.zoom(zoomIn);
    }, 500);
  }

  public stopZoom(): void {
    if (this.zoomIntervalId) {
      clearInterval(this.zoomIntervalId);
      this.zoomIntervalId = null;
    }
  }
  public async zoom(zoomIn: boolean): Promise<void> {
    const sm = this.sceneService.sceneManager;
    if (!sm) {
      return;
    }
    // Prevent overlapping zoom actions
    if (this.isZooming) {
      return;
    }
    try {
      this.isZooming = true;
      if (this.autoZoomEnabled) {
        this.toggleAutoZoom(true);
      }
      if (zoomIn) {
        await sm.zoomIn();
      } else {
        await sm.zoomOut();
      }
      if (this.socialAudioRelative) {
        this.multimediaRoomService.setAllVideoParticipantsSound();
      }
      this.checkBounds();
    } finally {
      // Allow the next zoom action
      this.isZooming = false;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
